/**
 * The mobile app owns the field schema for Procore-backed records.  The web
 * console deliberately reads those definitions instead of maintaining a
 * second, generic "record metadata" form that can drift from the phone.
 */

import {
  PROCORE_DAILY_LOG_CATEGORY_DEFINITIONS,
  type ProcoreDailyLogFieldDefinition,
} from "../../../backend/src/shared/procoreDailyLog";
import type { ConsoleRow } from "./api";

export interface MobileRecordField {
  key: string;
  label: string;
  value: string;
  multiline: boolean;
  calculated?: boolean;
}

function hasCategory(
  category: string,
): category is keyof typeof PROCORE_DAILY_LOG_CATEGORY_DEFINITIONS {
  return Object.prototype.hasOwnProperty.call(
    PROCORE_DAILY_LOG_CATEGORY_DEFINITIONS,
    category,
  );
}

/** The exact field definition the mobile item editor reads. Exporting it lets
 * the web editor render the same schema rather than maintain an approximation
 * beside the read-only formatter below. */
export function mobileRecordDefinition(
  item: Pick<ConsoleRow, "recordType" | "category"> & Partial<Pick<ConsoleRow, "syncSource">>,
) {
  if (!item.recordType || !item.category || !hasCategory(item.category))
    return null;
  const definition = PROCORE_DAILY_LOG_CATEGORY_DEFINITIONS[item.category];
  return item.syncSource === "autodesk" && item.category === "manpower"
    ? { ...definition, fields: autodeskManpowerFields(definition.fields) }
    : definition;
}

/**
 * Autodesk's Work Log has regular and overtime hours and no man-hours total,
 * so an Autodesk manpower record edits what its row holds: "Total Hours" is
 * Procore's derived column and is dropped, and `overtimeHours` (the key the
 * Form's "# OT hours" column reads) is offered beside the regular hours.
 */
function autodeskManpowerFields(
  fields: readonly ProcoreDailyLogFieldDefinition[],
): ProcoreDailyLogFieldDefinition[] {
  return fields
    .filter((field) => field.key !== "totalHours")
    .flatMap((field) => field.key === "hours"
      ? [
          { ...field, label: "Regular hours" },
          { ...field, key: "overtimeHours", label: "OT hours", requiredInProcore: false } as ProcoreDailyLogFieldDefinition,
        ]
      : [field]);
}

function titleCase(value: string): string {
  return value
    .trim()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function dateValue(value: string): string {
  const date = new Date(`${value.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

function formatFieldValue(
  field: ProcoreDailyLogFieldDefinition,
  value: unknown,
): string {
  if (value === null || value === undefined || value === "") return "Not set";
  if (field.type === "boolean") return value ? "Yes" : "No";
  const text = String(value);
  if (field.type === "date") return dateValue(text);
  // This matches the mobile detail formatter for the two controlled text
  // values that may arrive stored as normalized keys.
  if (field.key === "priority" || field.key === "status")
    return titleCase(text);
  return text;
}

/** `null` means the record is not backed by mobile's daily-log schema. */
export function mobileRecordFields(
  item: ConsoleRow,
): MobileRecordField[] | null {
  const definition = mobileRecordDefinition(item);
  if (!definition) return null;
  return definition.fields.map((field) => {
    const calculated = item.category === "manpower" && field.key === "totalHours";
    return {
      key: field.key,
      label: field.label,
      value: formatFieldValue(field, calculated ? manpowerTotalHours(item.fieldValues) : item.fieldValues?.[field.key]),
      multiline: field.type === "textarea",
      calculated,
    };
  });
}

/** Procore derives man-hours from headcount and hours per worker. A missing
 * input is unknown, while an explicitly entered zero is a valid total. */
function manpowerTotalHours(values: ConsoleRow["fieldValues"]): string | null {
  const inputs = [values?.workers, values?.hours];
  if (inputs.some((value) =>
    (typeof value !== "number" && typeof value !== "string") || String(value).trim() === "",
  )) return null;
  const total = Number(inputs[0]) * Number(inputs[1]);
  return Number.isFinite(total)
    ? total.toLocaleString("en-US", { maximumFractionDigits: 10 })
    : null;
}

export function mobileRecordKindLabel(
  item: Pick<ConsoleRow, "recordType" | "category">,
): string | null {
  if (item.recordType !== "observation") return null;
  return item.category === "observations" ? "Observation" : null;
}
